import { isTuningConfigurationHash, isTuningPatch, isTuningSettingKey, isTuningSettingValue, TUNING_STRATEGY_IDS } from './settings';
import type { TuningPatch, TuningSettingKey, TuningValue } from './settings';
import { isTuningTelemetry } from './measurements';
import type { TuningTelemetryWindow } from './measurements';
import { hasOnlyTuningKeys, isTuningArray, isTuningEnum, isTuningError, isTuningInteger, isTuningNumber,
  isTuningRecord, isTuningString, tuningJsonBytes } from './validation';

export type TuningIdentity = {
  sessionId: string; trialId: string; peerId: string; connectionEpoch: number; configRevision: number;
};
export type TuningAction =
  | { kind: 'apply-live'; patch: TuningPatch }
  | { kind: 'start-fresh'; patch: TuningPatch; strategyId: string; workloadId: string }
  | { kind: 'retry'; fromTrialId: string }
  | { kind: 'restore'; fromTrialId: string }
  | { kind: 'stop' };
export type TuningCommand = {
  version: 1; commandId: string; target: TuningIdentity; expectedRevision: number;
  leaseId: string; ttlMs: number; action: TuningAction;
};
export type TuningSettingReceipt = {
  key: TuningSettingKey; requested: TuningValue;
  accepted?: TuningValue; readback?: TuningValue; negotiated?: TuningValue;
  status: 'applied' | 'clamped' | 'unsupported' | 'requires-fresh-stream' | 'requires-host-restart' | 'failed';
  reason?: string;
};
export type TuningReceipt = {
  version: 1; commandId: string; target: TuningIdentity;
  stage: 'accepted' | 'applied' | 'observed' | 'rejected' | 'failed';
  settings: readonly TuningSettingReceipt[]; error?: { code: string; message: string }; policyVersion: string;
};
export type TuningTrialSnapshot = {
  target: TuningIdentity;
  phase: 'idle' | 'preparing' | 'replacing' | 'connecting' | 'settling' | 'measuring' |
    'completed' | 'failed' | 'restoring' | 'stopped';
  requested: TuningPatch; effective: TuningPatch; configurationHash: string;
  strategyId: string; workloadId: string; receipts: readonly TuningReceipt[];
  latest: readonly TuningTelemetryWindow[]; firstFrameMs?: number; guestFirstFrameMs?: number;
  restoration?: { cause: 'replacement-failure' | 'measurement-breach' | 'cancel' | 'stop';
    trialId: string; status: 'pending' | 'completed' | 'failed' };
  error?: { code: string; message: string };
};

export function isTuningIdentity(value: unknown): value is TuningIdentity {
  return isTuningRecord(value) && hasOnlyTuningKeys(value,
    ['sessionId', 'trialId', 'peerId', 'connectionEpoch', 'configRevision']) &&
    isTuningString(value['sessionId']) && isTuningString(value['trialId']) && isTuningString(value['peerId']) &&
    isTuningInteger(value['connectionEpoch']) && isTuningInteger(value['configRevision']);
}

export function isTuningAction(value: unknown): value is TuningAction {
  if (!isTuningRecord(value)) return false;
  switch (value['kind']) {
    case 'apply-live': return hasOnlyTuningKeys(value, ['kind', 'patch']) && isTuningPatch(value['patch']);
    case 'start-fresh': return hasOnlyTuningKeys(value, ['kind', 'patch', 'strategyId', 'workloadId']) &&
      isTuningPatch(value['patch']) && isTuningString(value['strategyId']) &&
      TUNING_STRATEGY_IDS.includes(value['strategyId']) && isTuningString(value['workloadId']);
    case 'retry':
    case 'restore': return hasOnlyTuningKeys(value, ['kind', 'fromTrialId']) && isTuningString(value['fromTrialId']);
    case 'stop': return hasOnlyTuningKeys(value, ['kind']);
    default: return false;
  }
}

export function isTuningCommand(value: unknown): value is TuningCommand {
  return isTuningRecord(value) && hasOnlyTuningKeys(value,
    ['version', 'commandId', 'target', 'expectedRevision', 'leaseId', 'ttlMs', 'action']) &&
    value['version'] === 1 && isTuningString(value['commandId']) && isTuningIdentity(value['target']) &&
    isTuningInteger(value['expectedRevision']) && value['expectedRevision'] === value['target'].configRevision &&
    isTuningString(value['leaseId']) && isTuningNumber(value['ttlMs'], 100, 10_000) && isTuningAction(value['action']) &&
    tuningJsonBytes(value) <= 16_384;
}

export function parseTuningCommand(value: unknown): TuningCommand | undefined {
  return isTuningCommand(value) ? value : undefined;
}

function isReceiptValue(value: unknown): value is TuningValue {
  return value === null || typeof value === 'boolean' || isTuningString(value, 256) ||
    (typeof value === 'number' && Number.isFinite(value));
}

export function isTuningSettingReceipt(value: unknown): value is TuningSettingReceipt {
  if (!isTuningRecord(value) || !hasOnlyTuningKeys(value,
    ['key', 'requested', 'accepted', 'readback', 'negotiated', 'status', 'reason'])) return false;
  return isTuningSettingKey(value['key']) && isTuningSettingValue(value['key'], value['requested']) &&
    isTuningEnum(value['status'], ['applied', 'clamped', 'unsupported', 'requires-fresh-stream', 'requires-host-restart', 'failed']) &&
    ['accepted', 'readback', 'negotiated'].every((key) => value[key] === undefined || isReceiptValue(value[key])) &&
    (value['reason'] === undefined || isTuningString(value['reason'], 2_048));
}

export function isTuningReceipt(value: unknown): value is TuningReceipt {
  return isTuningRecord(value) && hasOnlyTuningKeys(value,
    ['version', 'commandId', 'target', 'stage', 'settings', 'error', 'policyVersion']) && value['version'] === 1 &&
    isTuningString(value['commandId']) && isTuningIdentity(value['target']) &&
    isTuningEnum(value['stage'], ['accepted', 'applied', 'observed', 'rejected', 'failed']) &&
    isTuningArray(value['settings'], 25, isTuningSettingReceipt) &&
    (value['error'] === undefined || isTuningError(value['error'])) && isTuningString(value['policyVersion']);
}

function isRestoration(value: unknown): boolean {
  return isTuningRecord(value) && hasOnlyTuningKeys(value, ['cause', 'trialId', 'status']) &&
    isTuningEnum(value['cause'], ['replacement-failure', 'measurement-breach', 'cancel', 'stop']) &&
    isTuningString(value['trialId']) && isTuningEnum(value['status'], ['pending', 'completed', 'failed']);
}

export function isTuningTrialSnapshot(value: unknown): value is TuningTrialSnapshot {
  if (!isTuningRecord(value) || !hasOnlyTuningKeys(value, ['target', 'phase', 'requested', 'effective',
    'configurationHash', 'strategyId', 'workloadId', 'receipts', 'latest', 'firstFrameMs', 'guestFirstFrameMs', 'restoration', 'error'])) return false;
  return isTuningIdentity(value['target']) && isTuningEnum(value['phase'], ['idle', 'preparing', 'replacing', 'connecting', 'settling',
    'measuring', 'completed', 'failed', 'restoring', 'stopped']) &&
    isTuningPatch(value['requested']) && isTuningPatch(value['effective']) && isTuningConfigurationHash(value['configurationHash']) &&
    isTuningString(value['strategyId']) && isTuningString(value['workloadId']) &&
    isTuningArray(value['receipts'], 128, isTuningReceipt) && isTuningArray(value['latest'], 160, isTuningTelemetry) &&
    ['firstFrameMs', 'guestFirstFrameMs'].every((key) => value[key] === undefined || isTuningNumber(value[key])) &&
    (value['restoration'] === undefined || isRestoration(value['restoration'])) && (value['error'] === undefined || isTuningError(value['error']));
}
